import type { Driver } from '@cobliteam/api-lib';
import type { MediaRequestStatus as MediaRequestStatusV2 } from '@cobliteam/api-lib/src/__generated__/graphql';
import type { DateRange, TimestampInMillis } from './dates';
import type { UUID } from './identifier';
import type { Sort } from './pagination';
import type { User } from './user';
import type { Vehicle, VehicleGroup } from './vehicle';

export enum MediaType {
  TIMELAPSE = 'TIMELAPSE',
  EVENT_VIDEO = 'EVENT_VIDEO',
  FOOTAGE_VIDEO = 'FOOTAGE_VIDEO',
}

export enum EventSeverity {
  HIGHER = 'HIGHER',
  HIGH = 'HIGH',
  MEDIUM = 'MEDIUM',
  LOW = 'LOW',
}

export const IMAGE_EXTENSIONS: string[] = ['jpg', 'jpeg', 'png'];

export enum EventTrigger {
  TRAFFIC_SPEED_VIOLATED = 'TRAFFIC_SPEED_VIOLATED',
  MAX_SPEED_EXCEEDED = 'MAX_SPEED_EXCEEDED',
  STOP_SIGN_VIOLATED = 'STOP_SIGN_VIOLATED',
  LANE_DRIFT = 'LANE_DRIFT',
  ANOMALY = 'ANOMALY',
  UNKNOWN = 'UNKNOWN',
  COLLISION = 'COLLISION',
  /** Default Camera Event Triggers **/
  HARSH_CORNERING = 'HARSH_CORNERING',
  HARSH_BREAKING = 'HARSH_BREAKING',
  HARSH_ACCELERATION = 'HARSH_ACCELERATION',
  DISTRACTED_DRIVING = 'DISTRACTED_DRIVING',
  TAILGATING = 'TAILGATING',
  FRONT_COLLISION_WARNING = 'FRONT_COLLISION_WARNING',
  SOS = 'SOS',
  IGNITION_OFF_RECORD = 'IGNITION_OFF_RECORD',
  /** Premium Camera Event Triggers **/
  EYES_CLOSED = 'EYES_CLOSED',
  YAWN = 'YAWN',
  PHONE_USAGE = 'PHONE_USAGE',
  SMOKING = 'SMOKING',
}

export const DEFAULT_CAMERA_EVENT_TRIGGERS = [
  EventTrigger.HARSH_CORNERING,
  EventTrigger.HARSH_BREAKING,
  EventTrigger.HARSH_ACCELERATION,
  EventTrigger.DISTRACTED_DRIVING,
  EventTrigger.TAILGATING, // is the same as EventTrigger.FRONT_COLLISION_WARNING
  // EventTrigger.COLLISION, /** Not yet a default trigger. This is enabled by feature-flag for now **/
  EventTrigger.SOS,
];

export const PREMIUM_CAMERA_EVENT_TRIGGERS = [EventTrigger.EYES_CLOSED, EventTrigger.YAWN, EventTrigger.PHONE_USAGE, EventTrigger.SMOKING];

export enum SuggestedOccurrence {
  IS_DAYTIME = 'IS_DAYTIME',
  IS_AFTERNOON = 'IS_AFTERNOON',
  IS_NIGHT = 'IS_NIGHT',
  IS_SUNNY = 'IS_SUNNY',
  IS_CLOUDY = 'IS_CLOUDY',
  IS_RAINING = 'IS_RAINING',
  IS_HEAVY_TRAFFIC = 'IS_HEAVY_TRAFFIC',
  HAS_PASSENGER = 'HAS_PASSENGER',
  IS_DIRT_ROAD = 'IS_DIRT_ROAD',
  IS_USING_PHONE = 'IS_USING_PHONE',
}

export enum MediaRequestStatus {
  PENDING = 'PENDING',
  ACKNOWLEDGE = 'ACKNOWLEDGE',
  STARTED = 'STARTED',
  FINISHED = 'FINISHED',
  UNAVAILABLE = 'UNAVAILABLE',
  FAILED = 'FAILED',
  CANCELED = 'CANCELED',
  CORRUPTED = 'CORRUPTED',
}

export enum MediaWatchStatus {
  WATCHED = 'WATCHED',
  NEW = 'NEW',
}

export enum MediaRequestStatusFilter {
  PENDING = 'PENDING',
  WATCHED = 'WATCHED',
  NEW = 'NEW',
}

export enum MediaObstructedValues {
  OBSTRUCTED = 'OBSTRUCTED', // "Câmera da cabine obstruída" = true,
  UNOBSTRUCTED = 'UNOBSTRUCTED', // "Sem obstrução" = false
}

export enum IdentificationMethod {
  FACE_IDENTIFICATION = 'FACE_IDENTIFICATION',
  USER_EDIT = 'USER_EDIT',
  USER_CONFIRMATION = 'USER_CONFIRMATION',
  WITHOUT_IDENTIFICATION = 'WITHOUT_IDENTIFICATION',
  TRIP_WITHOUT_IMAGE = 'TRIP_WITHOUT_IMAGE',
}

export enum TripImageObstructionFeedback {
  CORRECT = 'CORRECT',
  BAD_LIGHTNING = 'BAD_LIGHTNING',
  DRIVER_WRONG_POSITION = 'DRIVER_WRONG_POSITION',
  DRIVER_AWAY = 'DRIVER_AWAY',
  OTHER_REASON = 'OTHER_REASON',
}

export interface TripImageDetails {
  id: UUID;
  imageUrl: string;
  isObstructed: boolean;
  obstructionFeedback: TripImageObstructionFeedback;
}

export interface TripImageUser {
  id: UUID;
  name: string;
}

export interface TripImageHistory {
  id: string;
  startTimestamp: TimestampInMillis;
  endTimestamp: TimestampInMillis;
  identificationMethod: IdentificationMethod;
  nominatedAt?: TimestampInMillis;
  nominatedBy?: TripImageUser;
  recognizedDriver?: Driver;
  tripId: string;
  tripImageSample?: TripImageDetails;
  tripImages?: TripImageDetails[];
  userNominatedDriver?: Driver;
  vehicle: Vehicle;
}

export interface VehicleTimestampImages {
  images: TripImageDetails[];
  vehicle: Vehicle;
}

export interface ClusterNominationData {
  clusterId: UUID;
  driverId?: UUID | undefined;
}

export enum CoachingFeedbackStatus {
  ADDING_EVIDENCES = 'ADDING_EVIDENCES',
  APPLYING = 'APPLYING',
  FINISHED = 'FINISHED',
}

export enum CoachingEvidenceType {
  MEDIA = 'MEDIA',
}

export enum EvidenceFileType {
  VIDEO_FRAME = 'VIDEO_FRAME',
  CUSTOM_UPLOAD = 'CUSTOM_UPLOAD',
  THUMBNAIL = 'THUMBNAIL',
}

export interface CoachingEvidenceFile {
  id: UUID;
  fileType: EvidenceFileType;
  fileUrl: string;
  fileTimestamp?: number;
  createdAt: string;
}

export interface CoachingEvidence {
  id: UUID;
  mediaId: UUID;
  media?: MediaView;
  safetyOccurrences: SafetyOccurrence[];
  type: CoachingEvidenceType;
  files?: CoachingEvidenceFile[];
}

export enum CoachingFeedbackAction {
  GUIDANCE = 'GUIDANCE',
  WARNING = 'WARNING',
  SUSPENSION = 'SUSPENSION',
  DISMISSAL = 'DISMISSAL',
  NO_ACTION = 'NO_ACTION',
}

export interface CoachingFeedback {
  id: UUID;
  coachingEvidences?: CoachingEvidence[];
  createdAt: string;
  createdByUser: Pick<User, 'fullName'>;
  driver?: Pick<Driver, 'name' | 'id'>;
  note?: string;
  noteAuthor?: Pick<User, 'fullName'>;
  noteCreatedAt?: string;
  status: CoachingFeedbackStatus;
  feedbackAction?: CoachingFeedbackAction;
  updatedAt?: string;
  updatedByUser?: Pick<User, 'fullName'>;
}

export interface UploadCoachingEvidenceFileResponse {
  message: string;
  code: string;
  coachingFileId?: UUID;
  coachingFileUrl?: string;
}

export interface SafetyOccurrence {
  id: UUID;
  name: string;
}

export interface SafetyReviewRequest {
  mediaId: UUID;
  id?: UUID;
  occurrencesIds: UUID[];
  driverId?: UUID;
  driver?: Pick<Driver, 'id' | 'name'>;
  status?: SafetyReviewStatus;
  severity?: EventSeverity;
  dismissReason?: SafetyReviewDismissReason;
  updatedByName: string;
}

export interface MediaCommentRequest {
  mediaId: UUID;
  commentId?: UUID;
  content?: string;
}

export enum SafetyReviewStatus {
  RELEVANT = 'RELEVANT',
  DISMISSED = 'DISMISSED',
}

export enum SafetyReviewDismissReason {
  NO_EVENT_OR_OCCURRENCE = 'NO_EVENT_OR_OCCURRENCE',
  WRONG_EVENT_OR_OCCURRENCE = 'WRONG_EVENT_OR_OCCURRENCE',
  BLURRY_OR_LOW_QUALITY = 'BLURRY_OR_LOW_QUALITY',
  EVENT_NOT_INTERESTING = 'EVENT_NOT_INTERESTING',
}

export interface MediaComment {
  id: UUID;
  createdBy: UUID;
  createdByUser?: Pick<User, 'fullName'>;
  content: string;
  createdAt: string;
  updatedAt: string;
  mediaId: UUID;
}

export interface SafetyReview {
  id: UUID;
  fleetId: UUID;
  status?: SafetyReviewStatus;
  driverId?: UUID;
  driver?: Pick<Driver, 'id' | 'name'>;
  severity?: EventSeverity;
  originalMediaSeverity?: EventSeverity;
  createdBy: string;
  updatedBy: string;
  updatedByUser: Pick<User, 'fullName'>;
  createdAt: string;
  updatedAt: string;
  occurrences?: SafetyOccurrence[];
  dismissReason?: SafetyReviewDismissReason;
}

export interface SafetyReviewSnapshot {
  driverId?: UUID;
  driver?: Driver;
  occurrences?: SafetyOccurrence[];
  status?: SafetyReviewStatus;
  severity?: EventSeverity;
  dismissReason?: SafetyReviewDismissReason;
}

export interface SafetyReviewHistory {
  id: UUID;
  createdAt: string;
  snapshot: SafetyReviewSnapshot;
  userId: UUID;
  user: User;
}

export interface AiVideoTags {
  seatbeltDetection?: { withSeatbelt: boolean } | null;
}

export interface Media {
  id: UUID;
  deviceId: string;
  driverId?: UUID;
  unreviewedDriver?: Pick<Driver, 'id' | 'name'>;
  driver?: Pick<Driver, 'id' | 'name'>;
  mediaType: MediaType;
  mediaDetails: MediaDetails;
  mediaViewCount: number;
  timestamp: TimestampInMillis;
  severity?: EventSeverity;
  severityDetail?: SeverityDetail;
  licensePlate?: string;
  isFavorite?: boolean;
  vehicle?: Vehicle;
  vehicleGroup?: VehicleGroup;
  mediaStatus?: MediaStatus;
  safetyReview?: SafetyReview;
  comments: MediaComment[];
  aiSuggestedDriver?: Pick<Driver, 'id' | 'name'>;
  aiVideoTags?: AiVideoTags;
}

export enum MediaStatus {
  WATCHED = 'WATCHED',
  NEW = 'NEW',
  RELEVANT = 'RELEVANT',
  DISMISSED = 'DISMISSED',
}

export enum VideoChannel {
  ROAD = 'ROAD',
  DRIVER = 'DRIVER',
  PIP = 'PIP',
  SIDE_BY_SIDE = 'SIDE_BY_SIDE',
  UNKNOWN = 'UNKNOWN',
}

export interface Video {
  url: string;
  channel: VideoChannel;
}

export interface MediaEvent {
  eventTrigger: EventTrigger;
  latitude: number;
  longitude: number;
  mediaUrl: string;
  speed: number;
  speedSign?: number;
  thumbnailUrl: string;
}

export interface MediaRequestUser {
  id: UUID;
  fullName: string;
  emailAddress: string;
  fleetId?: string;
}

export interface MediaRequest {
  createdAt: TimestampInMillis;
  duration: number;
  endLatitude?: number;
  endLongitude?: number;
  mediaUrl?: string;
  requestId: string;
  startLatitude?: number;
  startLongitude?: number;
  status: MediaRequestStatus;
  requestStatus: MediaRequestStatusV2;
  thumbnailUrl?: string;
  updatedAt: TimestampInMillis;
  processedAt?: TimestampInMillis;
  createdBy?: MediaRequestUser;
  createdByTrigger?: {
    id: string;
    name: string;
  };
}
export enum MediaRequestStatusFilterV2 {
  PENDING = 'Pendente',
  PROCESSING = 'Processando',
  COMPLETED = 'Concluída',
  FAILED = 'Falha',
}

export interface MediaFilters {
  page?: number;
  size?: number;
  sort?: Sort;
  mediaTypes?: MediaType[];
  vehicleIds?: UUID[];
  favoriteMedia?: boolean;
  driverIds?: UUID[];
  mediaPeriod?: DateRange;
  /**
   * @deprecated use mediaStatus instead
   */
  watchStatus?: MediaWatchStatus[];
  safetyOccurrenceIds?: UUID[];
  mediaStatus?: MediaStatus[];
}

export interface EventFilters extends MediaFilters {
  eventTriggers?: EventTrigger[];
  severityTypes?: EventSeverity[];
  descriptionTextQuery?: string;
  suggestedOccurrences?: string[];
}

export interface RequestFilters extends MediaFilters {
  requestStatus?: MediaRequestStatusFilterV2[];
  createdBy?: UUID[];
  requestedAt?: DateRange;
  isRequestedByTrigger?: boolean;
  triggerIds?: UUID[];
}
export interface CreateSharedMedia {
  mediaId: UUID;
}

export type MediaDetails = MediaEvent | MediaRequest;

export interface CreateMediaRequest {
  vehicleId: string;
  startTime: number;
  endTime: number;
  isTimelapse?: boolean;
  enableEmailNotification?: boolean;
  tripId?: string;
}

export interface CreateMediaRequestResponse {
  vehicleId: UUID;
  startTime: number;
  endTime: number;
  mediaId?: UUID;
}

export interface TripFilters {
  fromTimestamp?: TimestampInMillis;
  toTimestamp?: TimestampInMillis;
}

export interface Trip {
  id: string;
  overnightTripStartTimestamp?: TimestampInMillis;
  startTimestamp: TimestampInMillis;
  endTimestamp: TimestampInMillis;
  isFootageAvailable: boolean;
}

export enum AudioStatus {
  OFF = 'OFF',
  ON = 'ON',
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
}

export enum DutyType {
  LIGHT = 'LIGHT',
  MEDIUM = 'MEDIUM',
  HEAVY = 'HEAVY',
}

export interface Audio {
  audioStatus: AudioStatus;
  deviceId: string;
  updatedAt: string;
}

export interface UpdateAudioRequest {
  audioStatus: AudioStatus;
  deviceId: string;
}

export interface CameraConfiguration {
  audioStatus?: AudioStatus;
  deviceId: string;
  updatedAt?: string;
  dutyType?: DutyType;
  speedLimitWarningInKmph?: number;
}

export interface UpdateCameraConfigurationRequest {
  audioStatus?: AudioStatus;
  speedLimitWarningInKmph?: number;
  dutyType?: DutyType;
  deviceId: string;
}

export interface UpdateCameraSpeedLimit {
  deviceId: string;
  speedLimitWarningInKmph?: number;
  dutyType?: DutyType;
}

export interface Association {
  associationConfig: AssociationConfig;
  fleetId: string;
  updatedAt: string;
  lastUpdatedBy: string;
}

export interface AssociationConfig {
  entireFleetAssociationConfig: EntireFleetAssociationConfig;
}

export interface EntireFleetAssociationConfig {
  isOn: boolean;
}

export interface DriverPhoto {
  imageUrl: string;
}

export interface SampleImage {
  imageUrl: string;
  capturedAt: string;
  latitude?: number | null;
  longitude?: number | null;
  vehicle?: {
    id: UUID;
    licensePlate?: string | null;
    alias?: string | null;
    groups?: { id: UUID; name: string }[];
  } | null;
}

export interface DriverFaceCluster {
  id: UUID;
  driver?: Driver;
  suggestedDriver?: Driver;
  profileImageUrl: DriverPhoto;
  sampleImageUrls: DriverPhoto[];
  sampleImages: SampleImage[];
  sampleImageUrlCount: Number;
  createdAt: TimestampInMillis;
  updatedAt: TimestampInMillis;
}

export enum ImageAssignRequestSource {
  UPLOAD = 'UPLOAD',
  ASSIGN_CLUSTER = 'ASSIGN_CLUSTER',
  HIGH_CONFIDENCE_MATCH_WITH_UPLOAD_IMAGE = 'HIGH_CONFIDENCE_MATCH_WITH_UPLOAD_IMAGE',
  PATH_REVIEW = 'PATH_REVIEW',
}

export interface DriverFaceImageAssignRequest {
  url: string;
  driverId: UUID | null;
  useAsProfileImage?: boolean;
  source?: ImageAssignRequestSource;
}

export interface UpdateDriverFaceClusterRequest {
  driverFaceClusterId: UUID;
  driverId: UUID | null;
}

export interface ClusterPhoto {
  id: UUID;
  url: string;
  imageTimestamp: string;
  createdAt: string;
  deviceId: string;
  vehicleId?: UUID;
  isObstructed?: boolean;
  faceConfidence?: number;
}

export interface SuggestedClusterPhoto extends ClusterPhoto {
  clusterId?: UUID;
  similarityPercentage: number;
}

export interface DriverPhotosDetails {
  driverId: UUID;
  mainPhoto?: ClusterPhoto;
  clusterPhotos: ClusterPhoto[];
  suggestions: SuggestedClusterPhoto[];
}

export interface MediaView {
  mediaUrl?: string;
  expirationDate?: number;
  videos: Video[];
  mediaInfo: Media;
  aiDescription?: string;
  suggestedOccurrences: string[];
  aiVideoTags?: AiVideoTags;
}

export interface MediaDriver {
  id: string;
  name: string;
  phoneNumbers: string[];
}

export interface MediaShare {
  sharableMediaUrl: string;
  expirationTimestamp: TimestampInMillis;
}

export interface UserFavoriteMediaRequest {
  mediaId: UUID;
  isFavorite: boolean;
}

export interface DriverNominateTripImageInput {
  tripId: string;
  driverId: string | null;
  imageId: string;
  operation: NominationOperation;
  driverVisibility: DriverVisibility;
  imageUrl: string;
}

export enum NominationOperation {
  EDIT = 'EDIT',
  CONFIRM = 'CONFIRM',
}

export enum DriverVisibility {
  VISIBLE = 'VISIBLE',
  NOT_VISIBLE = 'NOT_VISIBLE',
}

export interface SendObstructionFeedbackInput {
  tripId: string;
  imageId: string;
  obstructionFeedback: TripImageObstructionFeedback;
}

export interface LivestreamRequest {
  deviceId: string;
}

export interface LivestreamResponse {
  urls: string[];
}

export interface FleetProducts {
  products: Product[];
}

export interface Product {
  id: UUID;
  name: string;
}

export enum EventCategory {
  ADAS = 'ADAS',
  DMS = 'DMS',
  ACCELEROMETER = 'ACCELEROMETER',
  IGNITION_OFF_RECORD = 'IGNITION_OFF_RECORD',
}

export interface WifiSettingsRequest {
  id?: UUID;
  ssid: String;
  password: String;
}

export interface WifiSettingsList {
  accessPoints: WifiSettings[];
}
export interface WifiSettings {
  id: UUID;
  ssid: string;
  createdAt: string;
  createdBy: string;
}

export interface EventsSeveritySummary {
  low: number;
  medium: number;
  high: number;
  higher: number;
}

export enum RiskFactorType {
  ROAD_SPEED_LIMIT = 'road-speed-limit',
  VEHICLE_SPEED_LIMIT = 'vehicle-speed-limit',
  NEARBY_ACCIDENTS = 'nearby-accidents',
  RISKY_HOUR = 'risky-hour',
}

export interface SeverityRiskFactor {
  id: UUID;
  type: RiskFactorType;
  value: number;
  customDetails: Record<string, any>;
}

export interface SeverityDetail {
  id: UUID;
  severityLevel: EventSeverity;
  severityScore: number;
  riskFactors: SeverityRiskFactor[];
}
